// @lumi/app-kit shared i18n vocabulary (ADR 0026 §7) — locale en.
// Byte-identical common.*/errors.* used across >=3 apps + owned by app-kit components.
// Apps merge appKitMessages under their own catalog; the no-shadow check forbids an
// app from redefining these keys. Generated from the per-app catalogs.
export default {
  // The sidebar's account menu + history buttons (suite design foundation, spec §4):
  // byte-identical across every chrome, so owned here rather than by nine app catalogs.
  accountMenu: {
    profile: 'My profile',
    settings: 'Settings',
    theme: 'Appearance',
    themeLight: 'Light',
    themeDark: 'Dark',
    support: 'Support',
    back: 'Back',
    forward: 'Forward',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
    // The account row of a phone-only account (issue 698): no name, no address.
    signedIn: 'Signed in',
  },
  // A sidebar entry another of the viewer's own roles opens (#1028): the
  // greyed entry's hint, its switch dialog, and the role list joiner.
  roleLock: {
    hint: 'Only as {roles}. Switch role: bottom left, your account → Role.',
    body: 'You have this area as {roles}. Right now you are working as {active}.',
    action: 'Open as {role}',
    where: 'You can switch your role any time: bottom left, your account → Role.',
    listOr: '{list} or {last}',
  },
  common: {
    // Every DateField's calendar and typing hint (issue 823; People's own until then).
    datePicker: {
      open: 'Open calendar',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      month: 'Month',
      year: 'Year',
      clear: 'Clear date',
      dayToken: 'DD',
      monthToken: 'MM',
      yearToken: 'YYYY',
    },
    deactivate: 'Deactivate',
    revoke: 'Revoke',
    remove: 'Remove',
    actions: 'Actions',
    calendar: 'Calendar',
    cancel: 'Cancel',
    close: 'Close',
    confirmTitle: 'Confirm',
    contactSupport: 'Contact support',
    copyLink: 'Copy link',
    edit: 'Edit',
    language: 'Language',
    linkCopied: 'Link copied',
    linkCopyFailed: 'Could not copy link',
    moduleListHeading: 'Your modules',
    moduleTeaserAction: 'Add in billing',
    moduleTeaserHeading: 'Also part of lumi',
    moduleTeaserNote: 'The workspace owner, the admins and the Billing Managers can add these modules.',
    moduleTeaserTag: 'Not in your plan',
    no: 'No',
    nothingHere: 'Nothing here yet',
    ok: 'OK',
    refresh: 'Refresh',
    retry: 'Try again',
    role: 'Role',
    save: 'Save',
    search: 'Search',
    switchModule: 'Switch module',
    tabsNext: 'Show next tabs',
    tabsPrevious: 'Show previous tabs',
    understood: 'Got it',
    userDeactivated: '{name} (deactivated)',
    yes: 'Yes',
  },
  errors: {
    // ADR 0036 code-first branch (errorKeyFor): a backend `code` wins over the
    // status. `invalid_credentials` is what every login door answers a bad
    // sign-in with — a bare 401 would otherwise fall through to
    // `errors.unauthorized`, telling someone who mistyped their password that
    // their session expired (issue 364, F10). It never lets a caller tell an
    // unknown account apart from a mistyped password — the two answer
    // identically — so the copy names both fields.
    // `MASTER_INVITE_REJECTED` is people-api's fallback when the identity
    // service refuses an invitation payload without a code of its own (issue
    // 364, decisions round 1): without an entry here it degrades to the
    // status-derived message, which says nothing about what to look at. Its
    // sibling `MASTER_TENANT_NOT_FOUND` stays local to PersonDetailView on
    // purpose — that one names a next step specific to that screen.
    // `PERSON_PHONE_INVALID`, `PERSON_ACCESS_PHONE_TAKEN` and
    // `PHONE_ACCESS_UNAVAILABLE` are people-api's "Give access" by mobile number
    // (issue 698): suite-wide here because the create forms reach the door too,
    // and their refusal goes to the app-wide channel, not to the dialog's notice.
    codes: {
      invalid_credentials: 'Sign-in failed. Please check your email address and password.',
      MASTER_INVITE_REJECTED: 'This invitation was refused because the person’s details could not be accepted. Check the email address and name on the person record, then try again.',
      PERSON_EMAIL_INVALID: 'This person has no valid email address, so no login can be invited. Correct the address on the person record first.',
      PERSON_PHONE_INVALID: 'This mobile number is not valid. Enter it in international format, starting with + and the country code, e.g. +49 151 23456789.',
      PERSON_ACCESS_PHONE_TAKEN: 'Another person already has a login with this mobile number, or is being given one. One number, one person.',
      PHONE_ACCESS_UNAVAILABLE: 'Access by mobile number is not available here — for this workspace or for this number’s country. Use an e-mail address instead.',
      SUITE_ROLE_NEEDS_EMAIL: 'This person signs in with a mobile number and has no e-mail address. Suite roles receive legal, billing and consent mail, so they need an address.',
      OWNER_NEEDS_EMAIL: 'This person signs in with a mobile number and has no e-mail address. The owner receives legal and contract mail, so ownership needs an address.',
      RECORD_CHANGED: 'Somebody else changed this record while you were editing it. Reload it — your input is kept as a draft.',
    },
    appUpdatedBody: 'This page could not finish loading because the app was updated. Please reload to continue.',
    appUpdatedTitle: 'A new version is available',
    conflict: 'This action conflicts with the current state. Please reload and try again.',
    contactSupport: 'Contact support',
    exhausted: 'This seems genuinely broken. We’re sorry. Please contact support.',
    failedTitle: 'Action failed',
    forbidden: 'You don’t have permission for this action.',
    loadFailedTitle: 'Could not be loaded',
    network: 'No connection to the server. Please check your internet connection.',
    notFound: 'The requested item was not found.',
    rateLimited: 'Too many requests. Please wait a moment and try again.',
    recordChangedReload: 'Somebody else saved this record after you opened it, so your save was not applied. Reload to see the current version — your input is kept and can be restored.',
    refreshFailedTitle: 'This may be out of date',
    server: 'A server error occurred. Please try again later.',
    timeout: 'The server did not answer in time. Please try again in a moment.',
    unauthorized: 'Your session has expired. Please sign in again.',
    unexpected: 'Something went wrong while displaying this. Please try again.',
    validation: 'Please check your input.',
  },
  notReady: {
    title: "This isn't ready for you yet",
    body: "Your workspace is still being prepared. We'll take you back to the workspace in 5 seconds.",
  },
  // The workspace this app was opened on does not exist, or can no longer be
  // reached (issue 364, F57). ONE message for BOTH causes on purpose: ats-api
  // answers a suspended tenant and an unknown slug identically, and splitting
  // the copy would confirm which workspaces exist. `body` therefore names no
  // cause, and nothing on the page names a workspace, a slug or a person.
  tenantUnavailable: {
    title: 'This workspace is not available',
    body: 'This workspace does not exist or cannot be reached right now — please double-check the address you opened.',
    accountLead: 'Your own data and your invoices stay available to you in your lumi account.',
    accountCta: 'Go to your lumi account',
    supportLead: 'If this is a mistake, get in touch and we will sort it out with you.',
    // The signed-in person's own membership is gone (issue 669): a revoked demo guest,
    // or a demo that ended under an open tab.
    ended: {
      title: 'You don’t have access to this workspace',
      body: 'You are signed in, but you are not (or no longer) a member of this workspace. If you were a guest in a demo, the demo has ended or your invitation was withdrawn.',
      accountLead: 'Your own lumi account is not affected.',
    },
  },
  billing: {
    banner: {
      suspendedTitle: 'Workspace locked',
      suspendedMessage: 'This workspace is suspended. Please contact us to restore access.',
      lockedBody: 'This workspace has been locked because of an open billing issue. Access to all modules is currently paused.',
      lockedSupportLead: 'To restore access, contact our support team:',
      lockedSupportCta: 'Contact support',
      lockoutOn: 'Otherwise it will be locked on {date}.',
      fixCardTitle: 'Update your card',
      fixCardBody: 'Your last payment was declined because your card was rejected.',
      fixCardCta: 'Update card',
      softDeclineTitle: 'Make sure your payment goes through',
      softDeclineBody: 'We couldn’t collect your last payment and will retry automatically. Make sure your payment method has enough funds.',
      softDeclineCta: 'Review payment method',
      scaTitle: 'Confirm your payment',
      scaBody: 'Your bank needs you to confirm this payment before we can collect it.',
      scaCta: 'Confirm payment',
      recoveryError: 'The payment could not be started right now. Please try again in a moment.',
      chargebackTitle: 'Settle your reversed payment',
      chargebackBody: 'Your bank reversed a payment, so it is outstanding again. Add a payment method to settle it.',
      chargebackCta: 'Add payment method',
      trialTitle: 'Add a payment method',
      trialBody: 'Your trial has ended. Add a payment method to keep this workspace active.',
      trialCta: 'Add payment method',
      reviewTitle: 'Review your billing',
      reviewBody: 'This workspace needs a billing action to stay active.',
      reviewCta: 'Manage billing',
      contactTitle: 'Contact your administrator',
      contactBody: 'A billing issue needs attention to keep this workspace active.',
    },
  },
  legalNoticeBanner: {
    title: 'Legal notices need your attention',
    employeeBody: 'Invitations are paused until the employee privacy notice is complete.',
    candidateBody: 'The career portal is offline until the candidate notices are complete.',
    imprintBody: 'The career portal is offline until an imprint is saved.',
    candidateImprintBody: 'The career portal is offline until the candidate notices are complete and an imprint is saved.',
    cta: 'Open Legal notices',
  },
  // The one "needs you" strip (issue 713): the list label, the hide control and the
  // per-item sentences. A lone legal item reuses legalNoticeBanner.* word for word.
  needsYou: {
    label: 'Still needs you:',
    hide: 'Hide',
    timeRules: {
      link: 'Working-time rules',
      cta: 'Check',
      body: 'The working-time rules have not been checked yet. lumi calculates with the statutory default rules and only warns until someone confirms that they fit your company.',
    },
    legal: { link: 'Fill in the legal notices' },
    billing: {
      link: 'Complete billing',
      cta: 'Open billing',
      bodyBoth: 'Add your invoice details and a payment method so your workspace keeps running without interruption.',
      bodyInvoiceDetails: 'Add your invoice details so your invoices are issued correctly.',
      bodyPaymentMethod: 'Add a payment method so your workspace keeps running without interruption.',
    },
  },
  // Consented temporary support access (ADR 0041): the marker every module
  // chrome shows beside the tenant name while a supporter is inside a grant.
  supportAccess: {
    label: 'Support Access',
    until: 'Temporary support access until {until} — everything you do here is recorded in this tenant\'s audit log.',
  },
  // Idle session timeout (issue 265). Owned by app-kit's IdleLogoutGuard, except
  // idleSignedOut — the login pages (workspace, system-admin) render that after
  // the bounce, and it is the same suite-wide policy statement, so it stays in one
  // place rather than drifting between the two login surfaces.
  session: {
    idleTitle: 'Are you still there?',
    idleBody: 'You have been inactive for a while. For your security, we will sign you out automatically.',
    idleStay: 'Stay signed in',
    idleSignedOut: 'You were signed out after {minutes} minutes of inactivity. Please sign in again.',
  },
  // Browser form drafts (owner-stamped, 7 days, typed values only — app-kit
  // utils/formDraftStorage.ts). Owned by app-kit's DraftBanner and by the
  // sign-out warning every module chrome shows, so the wording lives here instead
  // of drifting across five app catalogs.
  formDrafts: {
    bannerTitle: 'Unsaved draft',
    bannerSavedAt: 'Saved on this device: {when}',
    bannerRecordChanged: 'The record has changed since this draft. Restore puts back only the fields you changed.',
    restore: 'Restore',
    discard: 'Discard',
    closeTitle: 'Discard input in “{form}”?',
    closeBody: 'Unsaved input will be lost.',
    closeConfirm: 'Discard',
    closeCancel: 'Continue editing',
    recordChangedTitle: 'Changed by someone else',
    reload: 'Reload',
    forms: {
      'apprentice': {
        planStation: 'Training plan station',
        skillCatalogue: 'Skill catalogue',
        trainingData: 'Training details',
      },
      'ats': {
        workflowEditor: 'Workflow editor',
      },
      'backoffice': {
        applicationForm: 'Application',
        createApplication: 'New application',
        customField: 'Custom field',
        customFields: 'Custom fields',
        emailSettings: 'Email settings',
        jobAd: 'Job ad',
        jobAdTemplate: 'Job ad template',
        jobEdit: 'Job',
        portalSettings: 'Career portal settings',
        ravenBatchPublish: 'Batch job posting',
        ravenPublish: 'Job posting',
        role: 'Role',
        suggestion: 'Suggestion',
        supportTicket: 'Support ticket',
        supportTicketReply: 'Support ticket reply',
        talentEdit: 'Talent profile',
        workflowAction: 'Workflow action',
        workflowCondition: 'Workflow condition',
        workflowGuard: 'Workflow guard',
        workflowState: 'Workflow state',
        workflowTransition: 'Workflow transition',
      },
      'people': {
        bankAccount: 'Bank account',
        companyProfile: 'Company profile',
        employmentContract: 'Employment contract',
        myProfile: 'My profile',
        onboardingTemplate: 'Onboarding template',
        oneTimePayment: 'One-time payment',
        orgUnit: 'Organizational unit',
        person: 'Person',
        taxProfile: 'Tax profile',
        wageAssignment: 'Wage assignment',
      },
      'system-admin': {
        fileQuota: 'File quota',
        skillDetail: 'Skill detail',
        suggestionNotes: 'Suggestion notes',
      },
      'time': {
        blueprint: 'Working-time blueprint',
        leaveRequest: 'Leave request',
        leaveType: 'Leave type',
        orgGroup: 'Organizational group',
        project: 'Project',
        role: 'Role',
        userProfile: 'User profile',
        weekGrid: 'Week grid',
        workTimeModel: 'Work time model',
      },
      'user': {
        ssoSettings: 'SSO settings',
      },
      'workspace': {
        billingProfile: 'Billing profile',
        wizardGeneral: 'Setup wizard',
      },
    },
  },
  // Suite-wide legal footer (issue 221): the § 5 DDG imprint and the privacy
  // notice of the Lumi entity live once on the marketing site, and every app
  // links to them through the shared LegalLinksFooter. The wording is
  // byte-identical everywhere, so it belongs to app-kit rather than to any app.
  legal: {
    imprint: 'Imprint',
    privacy: 'Privacy',
    terms: 'Terms & Conditions',
    label: 'Legal information',
    employeeNotice: 'Privacy notice at {company}',
  },
  dataImport: {
    run: {
      stepsLabel: 'Import steps',
      steps: {
        file: 'File',
        questions: 'Questions',
        review: 'Review',
        commit: 'Apply',
      },
      toOverview: 'Back to overview',
      reading: '{file} is being read',
      checking: 'Rows are being checked',
      committing: 'Data is being applied',
      tables: '{count} table | {count} tables',
      rows: '{count} row | {count} rows',
      aiOn: 'Lumi improves the mapping',
      aiOff: 'Mapping by rules',
      proposalByRules: 'Proposal by rules · {reason}',
      reasons: {
        off: 'Lumi was switched off for this file.',
        disabled: 'AI is switched off in this workspace.',
        quota: 'The AI quota was used up.',
        no_key: 'No AI key is set up.',
        error: 'Lumi could not be reached just now.',
      },
    },
    title: 'Data import',
    intro: 'Import existing data from a legacy system: upload a file, review the field mapping, inspect the dry run, then commit.',
    backToList: 'Back to overview',
    sourceSystem: 'Source system',
    cancelled: 'This import was cancelled.',
    steps: {
      upload: 'Upload',
      analysis: 'Analysis',
      mapping: 'Mapping',
      dryRun: 'Dry run',
      commit: 'Commit',
    },
    status: {
      uploaded: 'Uploaded',
      analyzing: 'Analyzing',
      mapping_ready: 'Review mapping',
      dry_running: 'Dry run in progress',
      dry_run_ready: 'Dry run ready',
      committing: 'Committing',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    formats: {
      csv: 'CSV',
      xlsx: 'Excel',
      json: 'JSON',
      sql_dump: 'SQL dump',
      hropen: 'HR Open JSON',
    },
    transforms: {
      none: 'Direct',
      date: 'Date',
      number: 'Number',
      boolean: 'Yes/No',
      enum_map: 'Value mapping',
      split_name: 'Split name',
      concat: 'Concatenate',
      const: 'Constant',
      skip: 'Ignore',
    },
    upload: {
      sourceSystem: 'Source system name',
      sourceSystemHint: 'Stored as a reference so re-importing the same file updates records instead of duplicating them.',
      sourceSystemPlaceholder: 'e.g. legacy_hr_system',
      sourceSystemFirst: 'Please name the source system first.',
      dropHint: 'Drop a file here or browse',
      formats: 'CSV, Excel (XLSX), JSON or SQL dump — max. 50 MB',
      browse: 'Choose file',
      uploading: 'Uploading …',
    },
    running: {
      uploaded: 'Starting analysis …',
      analyzing: 'Analyzing file …',
      dry_running: 'Dry run in progress …',
      committing: 'Committing data …',
      hint: 'Large files can take a few minutes. This page refreshes automatically.',
    },
    failed: {
      title: 'Import failed',
      generic: 'The file could not be read.',
      unsupported: 'This file format is not supported. Use CSV, Excel, JSON, an SQL dump or HR Open.',
      empty: 'The file contains no rows.',
      enqueue: 'The import could not be started. Please try again in a moment.',
      dryRun: 'The rows could not be checked. Start the file again from the overview.',
      commit: 'The data could not be applied. Start the file again from the overview.',
    },
    questions: {
      quoted: '“{value}”',
      saving: 'The mapping is being saved',
      companionRunning: '{entity} are being created in {module}',
      reading: {
        title: 'What we found',
        taken: 'Will be imported',
        notTaken: 'Will not be imported',
        from: 'from column',
        nothingTaken: 'Nothing from this file will be imported yet.',
        split: '(split)',
        joined: '(combined)',
        reasons: {
          other_entry: 'belongs to another entry',
          never_importable: 'is never imported',
          no_field: 'no matching field',
        },
        wrong: 'Something not right?',
        editMapping: 'Change the mapping in detail',
        improve: 'Let Lumi improve it',
        improveConflict: 'The file cannot be read again right now. Reload the page and try again.',
      },
      extras: {
        title: 'The file also contains other data. Should it be imported too?',
        columns: 'From {columns}.',
        companion: 'From {columns}: {count} new value, created in {module} before the rest of the data arrives. | From {columns}: {count} new values, created in {module} before the rest of the data arrives.',
        companionTruncated: 'From {columns}: at least {count} new value, created in {module} before the rest of the data arrives. | From {columns}: at least {count} new values, created in {module} before the rest of the data arrives.',
        asOwnEntry: 'What is imported along with it shows up in the migration as an entry of its own (from {file}).',
        forbidden: 'You do not have permission in {module} for this. The import continues without this data.',
        unavailable: '{module} did not answer just now. The import continues without this data.',
      },
      list: {
        heading: 'We have nothing more to ask you | There is one thing we need to ask you | There are {count} things we need to ask you',
      },
      uncertain: {
        title: 'The column {column} contains {samples} — is that {field}?',
        titleNoSamples: 'The column {column} — is that {field}?',
        yes: 'Yes, that is {field}',
        no: 'No, it is …',
        pickField: 'Choose a field',
        skip: 'Do not import',
      },
      required: {
        title: 'For {entity} we need {field}. Where does it come from?',
        forAll: 'The same for everyone:',
        pickValue: 'Choose a value',
        inColumn: 'It is in column …',
        pickColumn: 'Choose a column',
        none: 'The file does not have it',
      },
      supervisor: {
        title: 'Who is the manager of these people?',
        fromColumn: 'From the column',
        columnHint: 'The employee number must appear in this file or already exist in People.',
        onePerson: 'One person for everyone:',
        pickPerson: 'Choose a person',
        noPersons: 'You cannot pick one person for everyone: the list of people is not available to you here.',
        self: 'Everyone leads themselves for now',
        selfHint: 'You build the org chart afterwards in People.',
        deferred: 'Without a supervisor for now',
        deferredHint: 'We flag the rows without a supervisor when checking.',
        personsTruncated: 'The first 500 people by name; you can also give the employee number of the person you want through a column.',
      },
      enum: {
        title: '{column} contains values that {module} does not know',
        count: '{count}×',
        pick: 'Please choose',
        leaveEmpty: 'leave empty',
        rowLabel: 'Mapping for {value}',
        viaCompanion: 'is created in {module}',
      },
      date: {
        title: 'How is the date in {column} written?',
        day: 'day',
        month: 'month',
        year: 'year',
        yearShort: 'year (2 digits)',
        example: '{sample} is read as {date}',
      },
      fold: {
        title: 'All mappings in detail ({count} column) | All mappings in detail ({count} columns)',
        show: 'Show',
        hide: 'Hide',
      },
      verdict: {
        title: 'This file cannot become {entry}',
        missing: 'Missing: {fields}. No column in the file could provide it.',
        extraHint: 'If you untick the additional data above, this no longer applies.',
        elsewhere: 'Choose another entry:',
        readAs: 'Read as {entry}',
        otherModule: 'If the file belongs in another module, discard it here and drop it there, where it belongs.',
        discard: 'Discard file',
      },
      footer: {
        next: 'Next we check every row. Nothing is saved yet.',
        nextCreating: 'First we create {count} new value ({what}) in {module}, then we check every row of your file. | First we create {count} new values ({what}) in {module}, then we check every row of your file.',
        back: 'Back',
        confirm: 'Next: check data',
        confirmCreating: 'Next: create and check',
        open: 'One question still open | {count} questions still open',
        withoutThem: 'You can continue without this data.',
      },
    },
    mapping: {
      hint: 'Review the proposed mapping: pick a target per table and a field per column. Highlighted rows are low-confidence guesses.',
      targetEntity: 'Target',
      skipTable: 'Do not import',
      skippedTable: 'This table will not be imported.',
      ignoreColumn: 'Ignore',
      sourceColumn: 'Column',
      sample: 'Sample values',
      targetField: 'Target field',
      transform: 'Transform',
      preview: 'Preview',
      rowCount: '{count} rows',
      patternHint: 'Date pattern, e.g. DD.MM.YYYY',
      multiTargetHint: 'Target {index} of {total} — the same file feeds several targets',
      valueMap: 'Map values',
      valueMapHint: 'Map the source values onto the values allowed in the system. Unmapped values produce one error per row in the dry run. Based on up to 20 distinct sample values of the column.',
      valueMapUnmapped: '— not mapped —',
      addColumnRow: 'Map a column again',
      customFieldBadge: 'Custom field',
      createCustomField: 'Create new custom field…',
      confirm: 'Confirm mapping & start dry run',
    },
    report: {
      dryRunHint: 'Dry run — nothing has been written yet. Check the counts and errors before committing.',
      completedHint: 'Import completed. The data has been written.',
      created: 'Created',
      updated: 'Updated',
      skipped: 'Skipped',
      errors: 'Errors',
      errorList: '{count} errors',
      downloadErrors: 'Download all errors',
      row: 'Row',
      errorMessage: 'Message',
      backToMapping: 'Back to mapping',
      commit: 'Commit data',
      commitConfirmTitle: 'Commit data?',
      commitConfirmBody: 'The import will now write to this module. New records are created, existing ones updated. This cannot be undone automatically.',
      commitStarted: 'Commit started — the data is being written.',
    },
    review: {
      title: 'This is what goes into the system. Want to change anything?',
      filterLabel: 'Filter rows',
      note: 'Changes apply to this import only. Your file stays as it is.',
      row: 'Row',
      status: 'Status',
      choose: 'Choose …',
      yes: 'Yes',
      no: 'No',
      noRows: 'No rows for this selection.',
      rowsNoun: 'Rows',
      tiles: {
        new: 'New',
        updated: 'Updated',
        toClear: 'To sort out',
        newNote: '{things} will be created',
        updatedNote: 'already exist, missing fields are filled in',
        toClearNote: 'stay out otherwise',
      },
      leftOut: '{count} row left out | {count} rows left out',
      filter: {
        all: 'All',
        skipped: 'Left out',
      },
      statuses: {
        new: 'new',
        updated: 'updated',
        open: 'open',
        corrected: 'corrected',
        skipped: 'left out',
      },
      fixField: 'Correct {field} in row {row}',
      reasonOf: 'Why row {row} is open',
      popoverLabel: 'Row {row}: what is wrong',
      mean: 'Did you mean {value}?',
      accept: 'Use {value}',
      findPerson: 'Find a person …',
      pickPerson: 'Choose a person',
      setForAll: 'Set for all {count} that match',
      skipRow: 'Leave this row out',
      leaveEmpty: 'Leave this field empty for this row',
      undo: 'Undo',
      reason: {
        required: '{field} is missing',
        missing: 'A required value is missing',
        invalid: 'The value does not fit',
        notFound: '{ref} not found',
        notFoundAny: 'Reference not found',
        ambiguous: 'Reference is not clear',
        emailLinked: 'This e-mail belongs to a login',
        general: 'Row not imported',
        skipped: 'Row left out',
      },
      changes: '{count} change saved | {count} changes saved',
      openRows: '{count} row still to sort out | {count} rows still to sort out',
      footer: {
        unchecked: '{changes} · {open}. Check again first, then import.',
        withdrawn: 'All changes are undone. Check again first, then import.',
        checked: '{open}. You can also import now, and this row stays out. | {open}. You can also import now, and these {count} stay out.',
        clear: 'Everything is sorted.',
      },
      back: 'Back to the questions',
      recheck: 'Check again',
      commit: 'Import {count} {things}',
      truncated: {
        all: 'Showing {shown} of {total} rows; all {open} to sort out are included.',
        some: 'Showing {shown} of {open} rows to sort out — the rest are in the error file.',
      },
      notice: {
        movedOn: 'The import has moved on in the meantime. This page now shows where it stands.',
        cell: 'This cell cannot be changed here.',
        value: 'The value does not fit.',
        generic: 'The change could not be saved.',
        notChecked: 'There are changes the check has not seen yet. Check again, then import.',
      },
      commitConfirmOpen: '{count} row stays out and is listed afterwards. | {count} rows stay out and are listed afterwards.',
    },
    // An import report's error row, worded from the facts the module sent (rowErrorText.ts).
    rowErrors: {
      required: '{field} is empty, but every row needs a value for it.',
      unknownField: '{field} cannot be imported here.',
      invalidEnum: '“{value}” is not a valid value for {field}. Valid values: {allowed}.',
      // With the review\'s handles: the cell holds the way out.
      invalidEnumCell: '“{value}” is not a valid value for {field}. Valid values: {allowed}. Choose an allowed value in the cell.',
      unresolved: {
        person: 'No person found whose {keys} is “{value}”.',
        supervisor: 'No manager found whose {keys} is “{value}”.',
        functionalSupervisor: 'No functional manager found whose {keys} is “{value}”.',
        orgUnit: 'No org unit found whose {keys} is “{value}”.',
        parentOrgUnit: 'No parent org unit found whose {keys} is “{value}”.',
      },
      supervisorRequired: 'No manager found whose {keys} is “{value}”, and this person has none yet. Every active person needs one: import the manager first, or name one who already exists.',
      unknownEntity: 'This kind of data cannot be imported here.',
      emailLinked: 'This person signs in to lumi with this e-mail address, so the import does not change it. The address stays as it is in the login — leave the field empty for this row to import the rest.',
      supervisorMissing: 'Every active person needs a manager, and this person has none. Name one in the file, or add the manager first.',
      transformFailed: '“{value}” in column {field} could not be converted. Check its format under “Transform” in the mapping step.',
      transformFailedNoValue: 'A value in column {field} could not be converted. Check its format under “Transform” in the mapping step.',
      unknown: 'This row was not imported. The details are in the error file.',
      skippedByUser: 'You left this row out, so it is not imported.',
      requiredAny: 'A required value is missing in this row.',
      invalidEnumAny: 'A value in this row is not one of the allowed values.',
      unknownFieldAny: 'A field in this row cannot be imported here.',
      unresolvedAny: 'Something this row refers to was not found. Create it first, or leave the row out.',
      unresolvedLocal: '{thing}: “{value}” was not found. Create it first, or leave the row out.',
      unresolvedUser: 'No person in this workspace matches this row.',
      invalidTime: 'The times in this row do not fit together.',
      invalidState: 'The status in this row does not exist in the published workflow.',
      stateChanged: 'The workflow changed while this row was being imported. Check again.',
      invalidCustomValue: 'A custom field value in this row does not fit.',
      ambiguous: {
        collision: '{ref}: “{value}” matches two different records — one as external ID, another as {other}. The import does not guess: choose the right record below.',
        otherSystem: '{ref}: “{value}” is not an external ID of this source system but one of “{system}” — and it also matches a record as {other}. The import does not guess: import with source system “{system}” if the same record is meant, or choose the right record below.',
        holderHasExternalId: '{ref}: “{value}” only matches as {other} — and that record already has the external ID “{otherId}” in this source system. The import does not guess: choose “{otherId}” below if it is meant.',
      },
      // Without the review's handles (the completed report lists the row; nothing here can be chosen).
      ambiguousPlain: {
        collision: '{ref}: “{value}” matches two different records — one as external ID, another as {other}. The import does not guess: make the reference unambiguous in the file.',
        otherSystem: '{ref}: “{value}” is not an external ID of this source system but one of “{system}” — and it also matches a record as {other}. The import does not guess: import with source system “{system}” if the same record is meant, or make the reference unambiguous.',
        holderHasExternalId: '{ref}: “{value}” only matches as {other} — and that record already has the external ID “{otherId}” in this source system. The import does not guess: refer to it with “{otherId}” if it is meant.',
      },
      localRefs: {
        department: 'Department',
        location: 'Location',
        candidate: 'Candidate',
        job: 'Job',
        client: 'Client',
        project: 'Project',
        bookingType: 'Booking type',
        leaveType: 'Leave type',
      },
      refs: {
        person: 'Person',
        supervisor: 'Manager',
        functionalSupervisor: 'Functional manager',
        orgUnit: 'Org unit',
        parentOrgUnit: 'Parent org unit',
      },
      supervisorInactive: {
        supervisor: 'The named manager is no longer active and so cannot lead anybody.',
        functional: 'The named functional manager is no longer active and so cannot lead anybody.',
      },
      supervisorUnconfirmed: {
        supervisor: 'The named manager has not been confirmed by HR yet and so cannot lead anybody. Confirm them in People first.',
        functional: 'The named functional manager has not been confirmed by HR yet and so cannot lead anybody. Confirm them in People first.',
      },
      datesInverted: 'The date under {end} is before the one under {start}.',
      agreementCodeInvalid: '{field} allows only letters, digits, “-”, “_” and “.”, at most 60 characters and no spaces.',
      statusTransition: 'A contract cannot move from “{from}” to “{to}”.',
      reportingCycle: 'This assignment would close the reporting line into a circle.',
      ambiguousOrgCode: 'Several org units of different types have the code “{value}”. Refer to the right one by its external ID.',
      keys: {
        externalId: 'external ID',
        employeeNumber: 'employee number',
        code: 'code',
      },
    },
    list: {
      title: 'Previous imports',
      file: 'File',
      system: 'Source system',
      status: 'Status',
      created: 'Created',
      empty: 'No imports yet.',
      open: 'Open',
      delete: 'Delete',
      deleteConfirmTitle: 'Delete import?',
      deleteConfirmBody: '“{file}” and all its import files will be removed. Data already committed is kept.',
      deleted: 'Import “{file}” deleted.',
    },
  },
  dataExport: {
    title: 'HR Open export',
    intro: 'Exports this module’s data as HR Open 4.5 (HR-JSON) — validated against the standard.',
    create: 'Create export',
    creating: 'Creating …',
    download: 'Download',
    password: {
      title: 'ZIP password — visible only now',
      hint: 'The export is created as a password-protected ZIP (AES-256) because it contains personal data. Copy the password now — it will not be shown again. Open with e.g. 7-Zip, WinZip or Keka.',
      copy: 'Copy password',
      copied: 'Copied',
    },
    empty: 'No exports yet.',
    columns: {
      standard: 'Standard',
      status: 'Status',
      items: 'Records',
      created: 'Created',
    },
    status: {
      queued: 'Queued',
      generating: 'Generating',
      completed: 'Completed',
      failed: 'Failed',
    },
  },
  // The demo's module picker (ADR 0083): the fork offers it on entry, the bar's
  // reset confirmation again. A locked module is booked (or core) and always in.
  // RoleNameTranslations.vue (ADR 0086): a custom role's names in other
  // languages, or the notice a system role gets instead.
  roleNames: {
    translations: 'Translations',
    translationsHint: 'The name in other languages. Empty fields show the name above.',
    systemHint: 'System role: lumi names it in every language. Its name and description cannot be changed; its permissions can.',
  },
  demoModules: {
    legend: 'Modules in the demo',
    booked: 'Booked — always included',
  },
  demoStage: {
    label: 'Demo',
    viewingAs: 'Viewing as',
    personaPlaceholder: 'Choose a role',
    persona: {
      team_lead: 'Team lead',
      employee: 'Employee',
      hr_admin: 'HR admin',
      recruiter: 'Recruiter',
      ats_admin: 'ATS admin',
      council_member: 'Works council member',
      apprentice: 'Apprentice',
      trainer: 'Trainer',
    },
    personaDoes: {
      team_lead: 'Decides on absence and correction requests, and rates applications.',
      employee: 'Books working days, requests absences, and has entries corrected.',
      hr_admin: 'Creates people, projects and jobs.',
      recruiter: 'Moves applications through first screening and schedules interviews.',
      ats_admin: 'Decides on absences, rates applications, and creates jobs.',
      council_member: 'Casts the works council vote on jobs and offers.',
      apprentice: 'Ticks off stations on the apprenticeship plan and books working days.',
      trainer: 'Reviews apprentice reports and schedules their exams.',
    },
    picker: {
      title: 'Whose view are you looking at the demo from?',
      subtitle: '{count} colleagues at {company}. Each role has its own tasks — your ticks are kept per role.',
      taskCount: '{total} task | {total} tasks',
      current: 'Current view',
    },
    intro: {
      lead: 'is made up. The people in it are made up too. Everything below the bar belongs to the demo — you take on different roles inside it. Your real account is untouched by it.',
      bridgeBefore: 'You can end the demo at any time. All the data below is then removed and you can set up ',
      bridgeAfter: ' for your own organization.',
      close: 'Close',
    },
    reset: 'Reset demo',
    guestMarker: 'Guest',
    guestWelcome: {
      invitedBy: 'A colleague from {workspace} invited you to this demo.',
      invitedAnon: 'A colleague invited you to this demo.',
      body: '{company} is made up and shared with everyone else in this demo. Others may have tried things already, so it can look half-played. “{reset}” gives everyone a fresh start. Your access ends when the demo ends. The people in it are made up too.',
    },
    cancel: 'Cancel',
    rebuildFailed: 'The demo could not be rebuilt. Please try again.',
    resetConfirm: {
      title: 'Reset the demo?',
      body: 'Everything {company} holds right now is thrown away — including anything you or your colleagues have entered, and everyone’s journey progress — and the demo is built again from scratch. Everyone in the demo starts fresh; nobody’s access changes.',
      cta: 'Reset demo',
    },
    // The demo bar's "Invite a colleague" dialog (issue 669).
    guests: {
      entry: 'Invite a colleague',
      title: 'Invite a colleague',
      subtitle: 'Colleagues you invite see this demo as it is and can try it hands-on.',
      close: 'Close',
      loading: 'Loading…',
      retry: 'Try again',
      emailLabel: 'Work email',
      emailPlaceholder: "name{'@'}company.com",
      send: 'Send invitation',
      sendAgain: 'Send again',
      slotsLeft: '{count} place left | {count} places left',
      exhausted: 'All {limit} places are taken. Revoke an invitation below to invite someone else.',
      invalid: 'Enter a valid email address.',
      listTitle: 'In this demo besides you',
      used: '{used} of {limit} used',
      empty: 'Nobody invited yet.',
      state: {
        invited: 'Invited',
        joined: 'Joined',
      },
      revoke: 'Revoke',
      revoking: 'Revoking…',
      revokeFor: 'Revoke access for {email}',
      about: {
        title: 'What a guest is',
        sees: 'A guest sees this demo exactly as it is right now — including everything you have already tried. “{reset}” in the bar gives everyone a fresh start.',
        resets: 'A guest can reset the demo too — for everyone in it.',
        limits: 'A guest can never end the demo or touch your real workspace.',
        ends: 'Guests are removed when the demo ends.',
      },
      feedback: {
        sent: 'Invitation sent to {email}.',
        resent: 'Invitation sent to {email} again.',
        undelivered: 'The email to {email} could not be delivered. {email} is on the list and holds a place — send again to retry.',
        revoked: 'Access for {email} was revoked. The place is free again.',
        alreadyMember: '{email} already has access to this workspace.',
        limit: 'All places are taken. Revoke an invitation to invite someone else.',
        rateLimited: 'Too many invitations today. Please try again later.',
        notLive: 'Invitations only work while the demo is live. It is being rebuilt, or setup has started — try again once the demo is back.',
        operator: 'You are signed in as a system operator. Operators cannot invite colleagues into a customer’s demo.',
        operatorNotMember: '{email} belongs to a lumi operator account, which cannot join a customer’s demo. Please invite a different address.',
        notAPerson: '{email} belongs to an account that is not a person, such as a service account. Please invite a colleague’s own work email.',
        forbidden: 'Only the workspace owner or a suite admin can invite colleagues.',
        noticeIncomplete: 'Invitations are paused until the employee notices under Legal notices are filled in completely.',
        accountClosed: 'That account has been closed and cannot be invited.',
        unavailable: 'Invitations cannot be counted right now. Please try again shortly.',
        revokeFailed: 'Access for {email} could not be revoked. Please try again.',
        generic: 'Something went wrong. Please try again.',
      },
    },
    leave: 'End demo',
    continueSetup: 'Continue setup',
    menu: 'Demo actions',
    signOut: 'Sign out',
    logoutHint: 'While the demo is on, sign out from the door or the menu in the demo bar.',
    journey: {
      title: '{name}’s journey',
      count: '{done} of {total} done',
      refresh: 'Refresh journey',
      unknown: 'Some checks could not be reached — showing the last known state.',
      yay: 'That is {name}’s whole journey — nicely done.',
      bridgeAdmin: 'You have walked every journey. When you are ready, end the demo and set up your own workspace.',
      bridgeUser: 'You have walked every journey. An admin can end the demo and set up your workspace.',
      blockedReset: 'A reset brings it back:',
      blocked: {
        gone: 'What this task needs is gone.',
      },
      next: 'Up next',
      nextCta: 'Open in {module}',
      footer: 'The demo ticks off what you do.',
    },
  },
  // The shared notifications popover (NotificationsPopover.vue). Its own namespace: the
  // apps' `notifications.*` keys (inbox page, preferences) are theirs and stay put.
  notificationsPopover: {
    title: 'Notifications',
    empty: 'You’re all caught up.',
    markAllRead: 'Mark all read',
    markRead: 'Mark read',
    markUnread: 'Mark unread',
    loading: 'Loading…',
    openTarget: 'Open the linked item',
    goToAll: 'Go to notifications',
  },
  // The shared place search (PlacePicker.vue). `noMatchAdd` is the wording for hosts whose
  // typed text IS the name (Enter adds it as typed); `noMatch` for hosts that keep it beside the field.
  placePicker: {
    placeholder: 'Search for a town or city …',
    keptHint: 'Saved exactly as you typed it — no place linked.',
    searching: 'Searching …',
    typeMore: 'Type at least {count} characters',
    noMatch: 'No match — your text is kept',
    error: 'Search failed',
    rateLimited: 'Too many searches — trying again shortly',
    resultsAnnouncement: 'No results | 1 result | {count} results',
    clear: 'Remove place',
    noMatchAdd: 'No town found — press Enter to add the name as typed',
  },
  // The suite's five app names and their one-line launcher descriptions, in this
  // language. THE one copy: component-lib's registry carries no displayed string, and
  // every app merges this underneath its own messages (ADR 0026 section 7).
  lumiModules: {
    suite: {
      name: 'Home',
      description: 'Your overview of the workplace',
    },
    ats: {
      name: 'Recruiting',
      description: 'Jobs, applications and the hiring process',
    },
    time: {
      name: 'Time',
      description: 'Time tracking, work-time accounts and approvals',
    },
    people: {
      name: 'People',
      description: 'Employee records, departments and locations',
    },
    apprentice: {
      name: 'Apprentices',
      description: 'Digital training records and apprenticeship plans',
    },
  },
};
